import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, Typography, Chip, IconButton, CircularProgress, Divider, Button, TextField, Slider, Tooltip } from '@mui/material';
import { ArrowBack, Refresh, LocationOn, GpsFixed, GpsOff, PlayArrow, Pause, SkipNext, SkipPrevious, ElectricScooter } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';
import vehicleImg from '../assets/vehicle.png';

const GMAP_KEY = 'AIzaSyBEkY7qnLR8cl0P2_dhmy5ytsZTxCTG67M';
const fmtSec = (s) => { if (!s||s<=0) return '—'; const m=Math.floor(s/60),h=Math.floor(m/60); return h>0?`${h}h ${m%60}m`:`${m}m`; };
const fmtMin = (m) => { m=parseFloat(m)||0; if (!m) return '0m'; const h=Math.floor(m/60),r=Math.round(m%60); return h>0?`${h}h${r?' '+r+'m':''}`:`${r}m`; };
const fmtAgo = (d) => { if (!d) return '—'; const df=Math.floor((Date.now()-new Date(d).getTime())/1000); if(df<60) return `${df}s ago`; if(df<3600) return `${Math.floor(df/60)}m ago`; return `${Math.floor(df/3600)}h ago`; };
const epochToTime = (ts) => { if (!ts) return '—'; return new Date((ts<1e10?ts*1000:ts)).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true}); };

export default function VehicleTrackDetail() {
  const navigate      = useNavigate();
  const { uuid }      = useParams();
  const [params]      = useSearchParams();
  const vehicleNumber = params.get('num') || '';

  // Map refs
  const mapRef         = useRef(null);
  const gMapRef        = useRef(null);
  const liveMarkerRef  = useRef(null);   // live tab vehicle marker
  const polylineRef    = useRef(null);   // history polyline
  const pathMarkerRef  = useRef(null);   // history playback vehicle marker
  const histStartRef   = useRef(null);   // history start dot marker
  const histEndRef     = useRef(null);   // history end dot marker
  const geocoderRef    = useRef(null);
  const liveTimerRef   = useRef(null);
  const playTimerRef   = useRef(null);
  const playIdxRef     = useRef(0);

  const [tab,         setTab]        = useState('live');
  const [telem,       setTelem]      = useState(null);
  const [todayData,   setTodayData]  = useState(null);
  const [address,     setAddress]    = useState(null);
  const [loading,     setLoading]    = useState(true);
  const [refreshing,  setRefreshing] = useState(false);
  const [mapLoaded,   setMapLoaded]  = useState(false);
  const [auto,        setAuto]       = useState(true);

  const [histDate,    setHistDate]   = useState(new Date().toISOString().split('T')[0]);
  const [histLoading, setHistLoading]= useState(false);
  const [histStats,   setHistStats]  = useState(null);
  const [histPts,     setHistPts]    = useState([]);
  const [playing,     setPlaying]    = useState(false);
  const [playIdx,     setPlayIdx]    = useState(0);

  // ── Clear all history map layers ─────────────────────────────────────────────
  const clearHistoryLayers = useCallback(() => {
    if (polylineRef.current)  { polylineRef.current.setMap(null);  polylineRef.current = null;  }
    if (pathMarkerRef.current){ pathMarkerRef.current.setMap(null); pathMarkerRef.current = null; }
    if (histStartRef.current) { histStartRef.current.setMap(null);  histStartRef.current = null;  }
    if (histEndRef.current)   { histEndRef.current.setMap(null);    histEndRef.current = null;    }
    if (playTimerRef.current) { clearInterval(playTimerRef.current); playTimerRef.current = null; }
    setPlaying(false); setPlayIdx(0); playIdxRef.current = 0;
  }, []);

  // ── API ───────────────────────────────────────────────────────────────────────
  const loadTelem = useCallback(async (silent=false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    const r = await InvokeApi(Config.base_url + apiList.loconavTelematics, {});
    if (r?.code === '200') {
      const t = (r.telematics||[]).find(t => t.vehicle_number===vehicleNumber || t.vehicle_uuid===uuid);
      if (t) setTelem(t);
    }
    setLoading(false); setRefreshing(false);
  }, [uuid, vehicleNumber]);

  const loadToday = useCallback(async () => {
    const r = await InvokeApi(Config.base_url + apiList.loconavVehicleDetail, { vehicle_uuid:uuid, vehicle_number:vehicleNumber });
    if (r?.code === '200') setTodayData(r.today || null);
  }, [uuid, vehicleNumber]);

  const loadHistory = useCallback(async (date) => {
    clearHistoryLayers();
    setHistLoading(true); setHistPts([]); setHistStats(null);
    try {
      const dbR = await InvokeApi(Config.base_url + apiList.loconavDbPath, { vehicle_number:vehicleNumber, date });
      if (dbR?.code==='200' && dbR.points?.length > 0) {
        setHistStats(dbR.stats); setHistPts(dbR.points.filter(p=>p.lat&&p.lng)); return;
      }
      const s = Math.floor(new Date(date+'T00:00:00').getTime()/1000);
      const e = Math.floor(new Date(date+'T23:59:59').getTime()/1000);
      const r2 = await InvokeApi(Config.base_url + apiList.loconavTimeline, { vehicle_uuid:uuid, start_time:s, end_time:e });
      if (r2?.code === '200') { setHistStats(r2.stats); setHistPts((r2.points||[]).filter(p=>p.lat&&p.lng)); }
    } finally { setHistLoading(false); }
  }, [vehicleNumber, uuid, clearHistoryLayers]);

  useEffect(() => { loadTelem(); loadToday(); }, [loadTelem, loadToday]);
  useEffect(() => { if (tab==='history') loadHistory(histDate); }, [tab, histDate, loadHistory]);
  useEffect(() => {
    if (liveTimerRef.current) clearInterval(liveTimerRef.current);
    if (auto && tab==='live') liveTimerRef.current = setInterval(()=>loadTelem(true), 30000);
    return () => { if (liveTimerRef.current) clearInterval(liveTimerRef.current); };
  }, [auto, loadTelem, tab]);

  // ── Google Maps ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (window.google?.maps) { setMapLoaded(true); return; }
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GMAP_KEY}`;
    s.async = true; s.onload = () => setMapLoaded(true);
    document.head.appendChild(s);
  }, []);

  const doGeocode = useCallback((lat, lng) => {
    if (!window.google?.maps || address || !lat || !lng) return;
    if (!geocoderRef.current) geocoderRef.current = new window.google.maps.Geocoder();
    geocoderRef.current.geocode({ location:{lat,lng} }, (res,st) => {
      if (st==='OK' && res[0]) setAddress(res[0].formatted_address);
    });
  }, [address]);

  const ensureMap = useCallback((center, zoom=15) => {
    if (!mapRef.current || !window.google) return null;
    if (!gMapRef.current) {
      gMapRef.current = new window.google.maps.Map(mapRef.current, {
        center, zoom, mapTypeControl:true, streetViewControl:false,
      });
    }
    return gMapRef.current;
  }, []);

  // ── Live tab map ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (tab!=='live' || !mapLoaded || !telem?.lat) return;
    // Remove history layers when switching to live
    clearHistoryLayers();
    const pos = { lat: telem.lat, lng: telem.lng };
    const map = ensureMap(pos, 15);
    if (!map) return;
    map.panTo(pos);
    const icon = { url:vehicleImg, scaledSize:new window.google.maps.Size(20,46), anchor:new window.google.maps.Point(10,23) };
    if (liveMarkerRef.current) {
      liveMarkerRef.current.setPosition(pos); liveMarkerRef.current.setIcon(icon);
    } else {
      liveMarkerRef.current = new window.google.maps.Marker({ position:pos, map, icon, title:vehicleNumber,
        label:{text:vehicleNumber, fontSize:'11px', fontWeight:'700', color:'#1a237e'} });
    }
    doGeocode(telem.lat, telem.lng);
  }, [mapLoaded, telem, tab, vehicleNumber, doGeocode, ensureMap, clearHistoryLayers]);

  // ── History tab map ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (tab!=='history' || !mapLoaded || !histPts.length) return;
    // Clear live marker
    if (liveMarkerRef.current) { liveMarkerRef.current.setMap(null); liveMarkerRef.current=null; }
    // Clear previous history layers
    clearHistoryLayers();

    const coords = histPts.map(p => ({ lat:parseFloat(p.lat), lng:parseFloat(p.lng) }));
    const map = ensureMap(coords[0], 12);
    if (!map) return;

    // Polyline
    polylineRef.current = new window.google.maps.Polyline({
      path:coords, geodesic:true, strokeColor:'#1a237e', strokeOpacity:.85, strokeWeight:4,
    });
    polylineRef.current.setMap(map);

    // Start marker (green dot)
    histStartRef.current = new window.google.maps.Marker({ position:coords[0], map, title:'Start',
      icon:{path:window.google.maps.SymbolPath.CIRCLE, scale:9, fillColor:'#16a34a', fillOpacity:1, strokeColor:'#fff', strokeWeight:2} });

    // End marker (red dot)
    histEndRef.current = new window.google.maps.Marker({ position:coords[coords.length-1], map, title:'End',
      icon:{path:window.google.maps.SymbolPath.CIRCLE, scale:9, fillColor:'#dc2626', fillOpacity:1, strokeColor:'#fff', strokeWeight:2} });

    // Playback vehicle marker
    pathMarkerRef.current = new window.google.maps.Marker({ position:coords[0], map, zIndex:100, title:vehicleNumber,
      icon:{ url:vehicleImg, scaledSize:new window.google.maps.Size(20,46), anchor:new window.google.maps.Point(10,23) },
      label:{text:vehicleNumber, fontSize:'10px', fontWeight:'700', color:'#1a237e'} });

    const b = new window.google.maps.LatLngBounds();
    coords.forEach(c => b.extend(c));
    map.fitBounds(b);
  }, [mapLoaded, histPts, tab, vehicleNumber, ensureMap, clearHistoryLayers]);

  // ── Playback ──────────────────────────────────────────────────────────────────
  const moveMarkerTo = useCallback((idx) => {
    if (!pathMarkerRef.current || !histPts[idx] || !gMapRef.current) return;
    const p = histPts[idx];
    const lat = parseFloat(p.lat), lng = parseFloat(p.lng);
    pathMarkerRef.current.setPosition({ lat, lng });
    gMapRef.current.panTo({ lat, lng });
    setPlayIdx(idx); playIdxRef.current = idx;
  }, [histPts]);

  const startPlay = useCallback(() => {
    if (playIdxRef.current >= histPts.length-1) { playIdxRef.current=0; setPlayIdx(0); }
    setPlaying(true);
    if (playTimerRef.current) clearInterval(playTimerRef.current);
    playTimerRef.current = setInterval(() => {
      const next = playIdxRef.current + 1;
      if (next >= histPts.length) { clearInterval(playTimerRef.current); setPlaying(false); return; }
      moveMarkerTo(next);
    }, 80);
  }, [histPts.length, moveMarkerTo]);

  const pausePlay = useCallback(() => {
    setPlaying(false); if (playTimerRef.current) clearInterval(playTimerRef.current);
  }, []);

  useEffect(() => () => {
    if (playTimerRef.current) clearInterval(playTimerRef.current);
    if (liveTimerRef.current) clearInterval(liveTimerRef.current);
  }, []);

  const isMoving = telem?.movement==='MOVING', ignOn=telem?.ignition==='ON';
  const statusColor = isMoving ? '#16a34a' : '#dc2626';

  return (
    <Box sx={{display:'flex',height:'calc(100vh - 80px)',overflow:'hidden'}}>
      {/* Left panel */}
      <Box sx={{width:380,flexShrink:0,display:'flex',flexDirection:'column',bgcolor:'#fff',borderRight:'1px solid #e2e8f0',height:'100%',overflow:'hidden'}}>
        {/* Header */}
        <Box sx={{px:2,py:1.5,borderBottom:'1px solid #f0f0f0',display:'flex',alignItems:'center',gap:1}}>
          <Tooltip title="Back">
          <IconButton onClick={() => navigate('/users')} sx={{ border: '1px solid #e8eaf6', borderRadius: 2 }}>
            <ArrowBack sx={{ color: '#1a237e' }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Vehicle Details</Typography>
          {/* <Typography variant="body2" color="text.secondary">
            ID: #{user.id} · {user.phone}
          </Typography> */}
        </Box>
          {tab==='live'&&<>
            <Chip size="small" label={auto?'● 30s':'○'} onClick={()=>setAuto(a=>!a)}
              sx={{cursor:'pointer',fontSize:10,fontWeight:700,bgcolor:auto?'#dcfce7':'#f1f5f9',color:auto?'#15803d':'#64748b'}}/>
            <IconButton size="small" onClick={()=>{loadTelem(true);loadToday();}}>
              <Refresh sx={{fontSize:17,color:'#1a237e',animation:refreshing?'spin 1s linear infinite':'none'}}/>
            </IconButton>
          </>}
        </Box>

        {/* Vehicle icon + number */}
        <Box sx={{px:2,pt:1.5,pb:0.5,display:'flex',alignItems:'center',gap:1.5}}>
          <ElectricScooter sx={{fontSize:26,color:'#1a237e'}}/>
          <Typography fontWeight={900} fontSize={22} sx={{fontFamily:'monospace',color:'#0f172a'}}>{vehicleNumber}</Typography>
        </Box>

        {/* Tabs */}
        <Box sx={{display:'flex',px:2,borderBottom:'2px solid #f0f0f0'}}>
          {['live','history'].map(t=>(
            <Box key={t} onClick={()=>setTab(t)} sx={{px:2,py:1,cursor:'pointer',fontWeight:700,fontSize:13,textTransform:'capitalize',
              color:tab===t?'#1a237e':'#94a3b8',borderBottom:`2px solid ${tab===t?'#1a237e':'transparent'}`,mb:'-2px',transition:'all .15s'}}>
              {t.charAt(0).toUpperCase()+t.slice(1)}
            </Box>
          ))}
        </Box>

        {/* Content */}
        <Box sx={{flex:1,overflowY:'auto',px:2,py:1.5}}>

          {/* ── LIVE ─────────────────────────────────────────── */}
          {tab==='live'&&(
            loading
            ? <Box sx={{display:'flex',justifyContent:'center',pt:6}}><CircularProgress size={28} sx={{color:'#1a237e'}}/></Box>
            : <Box>
                <Box sx={{display:'flex',alignItems:'center',gap:0.8,mb:0.5}}>
                  <Box sx={{width:10,height:10,borderRadius:'50%',bgcolor:statusColor,boxShadow:`0 0 0 3px ${statusColor}33`}}/>
                  <Typography fontSize={14} fontWeight={700} sx={{color:statusColor}}>
                    {isMoving?'Moving':`Stopped: ${fmtSec(telem?.stopped_sec)}`}
                  </Typography>
                </Box>
                <Typography fontSize={11} color="text.secondary" sx={{mb:1.2}}>Last data: {fmtAgo(telem?.last_updated)}</Typography>

                {address
                  ? <Box sx={{display:'flex',alignItems:'flex-start',gap:0.5,mb:0.8}}><LocationOn sx={{fontSize:13,color:'#1a237e',mt:0.1,flexShrink:0}}/><Typography fontSize={12} color="text.secondary">{address}</Typography></Box>
                  : telem?.lat
                  ? <Box sx={{display:'flex',alignItems:'center',gap:0.5,mb:0.8}}><GpsFixed sx={{fontSize:12,color:'#16a34a'}}/><Typography fontSize={12} color="text.secondary">{telem.lat?.toFixed(5)}, {telem.lng?.toFixed(5)}</Typography></Box>
                  : <Box sx={{display:'flex',alignItems:'center',gap:0.5,mb:0.8}}><GpsOff sx={{fontSize:12,color:'#94a3b8'}}/><Typography fontSize={12} color="text.secondary">No GPS</Typography></Box>
                }

                <Divider sx={{my:1.5}}/>
                <Box sx={{display:'flex',gap:1,mb:2}}>
                  {[{label:telem?.ignition??'OFF',sub:'Ignition',color:ignOn?'#15803d':'#dc2626',bg:ignOn?'#f0fdf4':'#fef2f2'},
                    {label:`${telem?.speed??0} km/h`,sub:'Speed',color:'#1e40af',bg:'#eff6ff'}
                  ].map(c=>(
                    <Box key={c.sub} sx={{flex:1,px:1.5,py:0.8,bgcolor:c.bg,borderRadius:1.5,textAlign:'center',border:`1px solid ${c.color}22`}}>
                      <Typography fontSize={14} fontWeight={800} sx={{color:c.color}}>{c.label}</Typography>
                      <Typography fontSize={10} color="text.secondary">{c.sub}</Typography>
                    </Box>
                  ))}
                </Box>

                <Divider sx={{my:1.5}}/>
                <Typography fontWeight={700} fontSize={13} color="#1a237e" sx={{mb:1.5}}>Today's Overview</Typography>
                {todayData
                  ? <Box sx={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:1}}>
                      {[
                        {label:'Travel Time',val:fmtMin(todayData.running_min),color:'#16a34a'},
                        {label:'Distance',val:`${todayData.distance_km||0} km`,color:'#0284c7'},
                        {label:'Stopped',val:fmtMin(todayData.stoppage_min),color:'#dc2626'},
                        {label:'Idle',val:fmtMin(todayData.idle_min),color:'#d97706'},
                      ].map(s=>(
                        <Box key={s.label} sx={{p:1,bgcolor:'#f8fafc',borderRadius:1.5,textAlign:'center',border:'1px solid #e2e8f0'}}>
                          <Typography fontSize={14} fontWeight={800} sx={{color:s.color}}>{s.val}</Typography>
                          <Typography fontSize={9} color="text.secondary" sx={{textTransform:'uppercase'}}>{s.label}</Typography>
                        </Box>
                      ))}
                    </Box>
                  : <Box sx={{textAlign:'center',py:3}}>
                      <Typography fontSize={12} color="text.secondary">No data for today</Typography>
                      <Typography fontSize={11} color="text.disabled">Sync from Fleet Report first</Typography>
                    </Box>
                }
                {telem?.lat&&(
                  <Button fullWidth variant="outlined" size="small" sx={{mt:2,borderColor:'#1a237e',color:'#1a237e',borderRadius:2,fontWeight:600}}
                    href={`https://www.google.com/maps?q=${telem.lat},${telem.lng}`} target="_blank">
                    Open in Google Maps
                  </Button>
                )}
              </Box>
          )}

          {/* ── HISTORY ──────────────────────────────────────── */}
          {tab==='history'&&(
            <Box>
              <TextField type="date" size="small" fullWidth value={histDate}
                onChange={e => setHistDate(e.target.value)}
                inputProps={{max:new Date().toISOString().split('T')[0]}}
                sx={{mb:2}}/>

              {histLoading
                ? <Box sx={{display:'flex',justifyContent:'center',pt:4}}><CircularProgress size={24} sx={{color:'#1a237e'}}/></Box>
                : histPts.length===0
                ? <Box sx={{textAlign:'center',py:5}}>
                    <Typography fontSize={13} color="text.secondary">No GPS data for {histDate}</Typography>
                    <Typography fontSize={11} color="text.disabled">Sync data from Fleet Report first</Typography>
                  </Box>
                : <>
                    {/* Day stats */}
                    {histStats&&(
                      <Box sx={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:1,mb:2}}>
                        {[
                          {label:'Distance',val:`${histStats.distance_km||0} km`,color:'#0284c7'},
                          {label:'Running',val:fmtMin(histStats.running_time_min),color:'#16a34a'},
                          {label:'Avg Speed',val:`${histStats.avg_speed_kmh||0} km/h`,color:'#1a237e'},
                          {label:'Idle',val:fmtMin(histStats.idle_min),color:'#d97706'},
                        ].map(s=>(
                          <Box key={s.label} sx={{p:1,bgcolor:'#f8fafc',borderRadius:1.5,textAlign:'center',border:'1px solid #e2e8f0'}}>
                            <Typography fontSize={14} fontWeight={800} sx={{color:s.color}}>{s.val}</Typography>
                            <Typography fontSize={9} color="text.secondary" sx={{textTransform:'uppercase'}}>{s.label}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Playback */}
                    <Box sx={{bgcolor:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:2,p:1.5,mb:2}}>
                      <Typography fontWeight={700} fontSize={12} color="#1a237e" sx={{mb:1}}>Path Playback</Typography>
                      <Box sx={{display:'flex',justifyContent:'space-between',mb:0.5}}>
                        <Typography fontSize={11} color="text.secondary">{epochToTime(histPts[playIdx]?.timestamp)}</Typography>
                        <Typography fontSize={11} color="text.secondary">{playIdx+1}/{histPts.length}</Typography>
                      </Box>
                      <Slider size="small" min={0} max={histPts.length-1} value={playIdx}
                        onChange={(_,v)=>{ pausePlay(); moveMarkerTo(v); }}
                        sx={{color:'#1a237e',mb:0.5}}/>
                      <Box sx={{display:'flex',justifyContent:'center',gap:1,mt:0.5}}>
                        <IconButton size="small" onClick={()=>{pausePlay();moveMarkerTo(0);}} sx={{color:'#1a237e'}}><SkipPrevious sx={{fontSize:20}}/></IconButton>
                        <IconButton size="small" onClick={playing?pausePlay:startPlay}
                          sx={{color:'#fff',bgcolor:'#1a237e',width:36,height:36,'&:hover':{bgcolor:'#1565c0'}}}>
                          {playing?<Pause sx={{fontSize:20}}/>:<PlayArrow sx={{fontSize:20}}/>}
                        </IconButton>
                        <IconButton size="small" onClick={()=>{pausePlay();moveMarkerTo(histPts.length-1);}} sx={{color:'#1a237e'}}><SkipNext sx={{fontSize:20}}/></IconButton>
                      </Box>
                    </Box>

                    {/* Start / End times */}
                    <Box sx={{display:'flex',gap:1}}>
                      {[
                        {label:'Start',ts:histPts[0]?.timestamp,color:'#16a34a'},
                        {label:'End',ts:histPts[histPts.length-1]?.timestamp,color:'#dc2626'},
                      ].map(s=>(
                        <Box key={s.label} sx={{flex:1,p:1,bgcolor:'#f8fafc',borderRadius:1.5,border:'1px solid #e2e8f0',textAlign:'center'}}>
                          <Box sx={{display:'flex',alignItems:'center',justifyContent:'center',gap:0.5,mb:0.2}}>
                            <Box sx={{width:7,height:7,borderRadius:'50%',bgcolor:s.color}}/>
                            <Typography fontSize={11} fontWeight={700} sx={{color:s.color}}>{s.label}</Typography>
                          </Box>
                          <Typography fontSize={13} fontWeight={700} color="#0f172a">{epochToTime(s.ts)}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
              }
            </Box>
          )}
        </Box>
      </Box>

      {/* Map */}
      <Box sx={{flex:1,height:'100%',position:'relative'}}>
        <Box ref={mapRef} sx={{width:'100%',height:'100%'}}>
          {!mapLoaded&&<Box sx={{display:'flex',justifyContent:'center',alignItems:'center',height:'100%',bgcolor:'#e5e7eb'}}><CircularProgress sx={{color:'#1a237e'}}/></Box>}
        </Box>
      </Box>

      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </Box>
  );
}
